import { CommonModule, NgOptimizedImage } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

// Directives
// Material Modules
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { RouterModule } from '@angular/router';
import { BlockClientCardComponent } from '@core-app/blocks/block-card/block-card.component';
import { BlockCardsWrapperComponent } from '@core-app/blocks/block-cards-wrapper/block-cards-wrapper.component';
import { BlockChatComponent } from '@core-app/blocks/block-chat/block-chat.component';
import { BlockH1TitleComponent } from '@core-app/blocks/block-h1-title/block-h1-title.component';
import { BlockHomeSkinnyCtaBannerComponent } from '@core-app/blocks/block-home-skinny-cta-banner/block-home-skinny-cta-banner.component';
import { BlockProfileComponent } from '@core-app/blocks/block-profile/block-profile.component';
import { BlockReadabilityModalComponent } from '@core-app/blocks/block-readability/block-readability-modal.component';
import { BlockReadabilityComponent } from '@core-app/blocks/block-readability/block-readability.component';
import { BlockClientSearchResultsAllComponent } from '@core-app/blocks/block-search-results-all/block-search-results-all.component';
import { BlockSplashLandingComponent } from '@core-app/blocks/block-splash-landing-page/block-splash-landing-page.component';
import { BlockTabGroupComponent } from '@core-app/blocks/block-tab-group/block-tab-group.component';
import { BlockUserAaTableComponent } from '@core-app/blocks/block-user-aa-table/block-user-aa-table.component';
import { BlockUserDocumentsComponent } from '@core-app/blocks/block-user-documents/block-user-documents.component';
import { BlockUserScoringComponent } from '@core-app/blocks/block-user-scoring/block-user-scoring.component';
import { BlockUserSmpiTableComponent } from '@core-app/blocks/block-user-smpi-table/block-user-smpi-table.component';
import { BrandLogoComponent } from '@core-app/layout/brand-logo/brand-logo.component';
import { TokenEntryComponent } from '@core-app/layout/login/token-entry.component';
import { SearchFormComponent } from '@core-app/layout/search/search-form/search-form.component';
import { SearchResultsComponent } from '@core-app/layout/search/search-results/search-results.component';
import { BlockAiFaqComponent } from '@core-app/blocks/block-ai-faq/block-ai-faq.component';
import { SearchComponent } from '@core-app/layout/search/search.component';
import { CoreChatModule } from '@gallaghercp/core-chat';
import { BlockWistiaVideoComponent } from '@core-app/blocks/block-wistia-video/block-wistia-video.component';
import { BlockLoginLandingComponent } from '@core-app/blocks/block-login-landing/block-login-landing.component';

// Custom Modules & Services
import { BlockService, DemoConfigModule, PageModule, SpinnerModule, UtilsModule } from '@gallaghercp/core-frontend';
import { SwiperModule } from '@gallaghercp/core-swiper';
import { TranslateModule } from '@ngx-translate/core';
import { AlertModule } from 'ngx-bootstrap/alert';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { CarouselModule } from 'ngx-bootstrap/carousel';
import { CollapseModule } from 'ngx-bootstrap/collapse';

// Bootstrap Modules
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { NgxMaskDirective } from 'ngx-mask';
import { ClientModule } from '../client/client.module';
import { BackToTopComponent } from './back-to-top/back-to-top.component';
import { DarkModeToggleComponent } from './dark-mode-toggle/dark-mode-toggle.component';
import { FooterComponent } from './footer/footer.component';
import { HeaderComponent } from './header/header.component';
import { HeaderMinimisedComponent } from './header-minimised/header-minimised.component';
import { LoginFaqsComponent } from './login-faqs/login-faqs.component';
import { LoginFooterComponent } from './login-footer/login-footer.component';
import { ActivateComponent } from './login/activate.component';
import { LoginComponent } from './login/login.component';

// App Components
import { MainComponent } from './main/main.component';
import { MaintenanceComponent } from './maintenance/maintenance.component';
import { SideNavFooterComponent } from './nav/side-nav/side-nav-footer/side-nav-footer.component';
import { SideNavComponent } from './nav/side-nav/side-nav.component';
import { PostThumbnailsComponent } from './nav/top-nav/post-thumbnails/post-thumbnails.component';
import { TopNavComponent } from './nav/top-nav/top-nav.component';
import { TopNavInlineComponent } from './nav/top-nav-inline/top-nav-inline.component';
import { TopNavUtilitiesComponent } from './nav/top-nav-utilities/top-nav-utilities.component';
import { NotificationBannerComponent } from './notification-banner/notification-banner.component';
import { NotificationsComponent } from './notifications/notifications.component';
import { OopsComponent } from './oops/oops.component';
import { PostComponent } from './post/post.component';
import { RegisterComponent } from './register/register.component';
import { ResetConfirmComponent } from './reset/reset-confirm.component';
import { ResetRequestComponent } from './reset/reset-request.component';
import { SearchV2Component } from '@core-app/layout/search/search-v2/search-v2.component';
import { BlockTestimonialComponent } from '@core-app/blocks/block-testimonial/block-testimonial.component';
import { BlockCtaBannerComponent } from '@core-app/blocks/block-cta-banner/block-cta-banner.component';
import { BlockHomeHeroBannerV2Component } from '@core-app/blocks/block-home-hero-banner-v2/block-home-hero-banner-v2.component';
import { BlockCalloutComponent } from '@core-app/blocks/block-callout/block-callout.component';
import { BlockLeaveOfAbsenceToolComponent } from '@core-app/blocks/block-leave-of-absence-tool/block-leave-of-absence-tool.component';
import { BlockBenefitPillarsSelectComponent } from '@core-app/blocks/block-benefit-pillars-select/block-benefit-pillars-select.component';
import { BlockCustomCardsComponent } from '@core-app/blocks/block-custom-cards/block-custom-cards.component';
import { IconComponent } from '@gallaghercp/core-components/icon';
import { BlockClientLanguageComponent } from '@core-app/blocks/block-language/block-language.component';
import { BlockCardCalloutsComponent } from '@core-app/blocks/block-card-callouts/block-card-callouts.component';
import { BlockAccordionComponent } from '@core-app/blocks/block-accordion/block-accordion.component';
import { BlockTimelineComponent } from '@core-app/blocks/block-timeline/block-timeline.component';
import { BlockOpenEnrollmentBannerComponent } from '@core-app/blocks/block-open-enrollment-banner/block-open-enrollment-banner.component';
import { BlockCarouselComponent } from '@core-app/blocks/block-carousel/block-carousel.component';
import { ButtonComponent } from '@gallaghercp/core-components/button';
import { PulloutComponent } from '@gallaghercp/core-components/pullout';
import { BlockFeatureCardGridComponent } from '@core-app/blocks/block-feature-card-grid/block-feature-card-grid.component';
import { BlockHeroBannerSplitFeatureComponent } from '@core-app/blocks/block-hero-banner-split-feature/block-hero-banner-split-feature.component';
import { BlockContentPageHeroBannerComponent } from '@core-app/blocks/block-content-page-hero-banner/block-content-page-hero-banner.component';
import { BlockContentSelectionComponent } from '@core-app/blocks/block-content-selection/block-content-selection.component';
import { BlockPulloutComponent } from '@core-app/blocks/block-pullout/block-pullout.component';
import { BlockAnnouncementBannerComponent } from '@core-app/blocks/block-announcement-banner/block-announcement-banner.component';
import { BlockHomeHeroBannerComponent } from '@core-app/blocks/block-home-hero-banner/block-home-hero-banner.component';
import { BlockHomeHeroBannerLegacyComponent } from '@core-app/blocks/block-home-hero-banner-legacy/block-home-hero-banner-legacy.component';
import { HeroOverlayLayoutComponent } from '@core-app/elements/hero-banner/hero-overlay-layout/hero-overlay-layout.component';
import { HeroSplitLayoutComponent } from '@core-app/elements/hero-banner/hero-split-layout/hero-split-layout.component';
import { BlockContentHeroBannerComponent } from '@core-app/blocks/block-content-hero-banner/block-content-hero-banner.component';
import { BlockContactModalComponent } from '@core-app/blocks/block-contact-modal/block-contact-modal.component';
import { BlockResourcesListComponent } from '@core-app/blocks/block-resources-list/block-resources-list.component';
import { ModalComponent } from '@gallaghercp/core-components/modal';
import { SafeHtmlPipe } from '../pipes/safe-html.pipe';
import { BlockCardEventsComponent } from '@core-app/blocks/block-card-events/block-card-events.component';

@NgModule({
    declarations: [
        MainComponent,
        MaintenanceComponent,
        OopsComponent,
        HeaderComponent,
        HeaderMinimisedComponent,
        FooterComponent,
        SideNavComponent,
        SideNavFooterComponent,
        TopNavComponent,
        TopNavInlineComponent,
        TopNavUtilitiesComponent,
        NotificationBannerComponent,
        ActivateComponent,
        LoginComponent,
        RegisterComponent,
        BlockProfileComponent,
        LoginFooterComponent,
        ResetRequestComponent,
        ResetConfirmComponent,
        LoginFaqsComponent,
        TokenEntryComponent,
        BackToTopComponent,
        NotificationsComponent,
        PostComponent,
        PostThumbnailsComponent,
        DarkModeToggleComponent,
        BlockChatComponent,
        BlockH1TitleComponent,
        BlockReadabilityComponent,
        BlockReadabilityModalComponent,
        BlockSplashLandingComponent,
        BlockClientCardComponent,
        BlockCardsWrapperComponent,
        BlockClientSearchResultsAllComponent,
        SearchResultsComponent,
        BlockAiFaqComponent,
        SearchComponent,
        SearchFormComponent,
        BlockUserScoringComponent,
        BrandLogoComponent,
        BlockHomeSkinnyCtaBannerComponent,
        BlockUserDocumentsComponent,
        BlockUserSmpiTableComponent,
        BlockUserAaTableComponent,
        BlockTestimonialComponent,
        BlockCtaBannerComponent,
        BlockHomeHeroBannerV2Component,
        BlockCalloutComponent,
        BlockBenefitPillarsSelectComponent,
        BlockCustomCardsComponent,
        BlockWistiaVideoComponent,
        BlockClientLanguageComponent,
        BlockFeatureCardGridComponent,
        BlockHeroBannerSplitFeatureComponent,
        BlockContentPageHeroBannerComponent,
        BlockLoginLandingComponent,
        BlockContentSelectionComponent,
        BlockAnnouncementBannerComponent,
        BlockCardCalloutsComponent,
        BlockOpenEnrollmentBannerComponent,
        BlockCarouselComponent,
        BlockHomeHeroBannerComponent,
        BlockHomeHeroBannerLegacyComponent,
        HeroOverlayLayoutComponent,
        HeroSplitLayoutComponent,
        BlockContentHeroBannerComponent,
        BlockContactModalComponent,
        BlockResourcesListComponent,
        BlockCardEventsComponent,
    ],
    imports: [
        CommonModule,
        RouterModule,
        BsDropdownModule,
        CollapseModule,
        ModalModule,
        ButtonsModule,
        CarouselModule,
        TabsModule,
        FormsModule,
        ReactiveFormsModule,
        MatProgressBarModule,
        SpinnerModule,
        AlertModule,
        TranslateModule,
        MatTabsModule,
        PageModule,
        MatExpansionModule,
        UtilsModule,
        TooltipModule,
        ClientModule,
        SwiperModule,
        CoreChatModule,
        DemoConfigModule,
        InfiniteScrollDirective,
        NgOptimizedImage,
        NgxMaskDirective,
        BlockTabGroupComponent,
        SearchV2Component,
        BlockLeaveOfAbsenceToolComponent,
        IconComponent,
        ButtonComponent,
        PulloutComponent,
        ModalComponent,
        SafeHtmlPipe,
    ],
    exports: [
        MainComponent,
        CommonModule,
        HeaderComponent,
        HeaderMinimisedComponent,
        NotificationsComponent,
        FooterComponent,
        BackToTopComponent,
        DarkModeToggleComponent,
        LoginFooterComponent,
        SideNavComponent,
        PostThumbnailsComponent,
        SearchComponent,
        SearchFormComponent,
        SafeHtmlPipe,
        MaintenanceComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class LayoutModule {
    constructor(protected blocks: BlockService) {
        this.blocks.addBlock('SPLASH-LANDING', BlockSplashLandingComponent);
        this.blocks.addBlock('HOME-HERO-BANNER', BlockHomeHeroBannerLegacyComponent);
        this.blocks.addBlock('CARD', BlockClientCardComponent);
        this.blocks.addBlock('CARDS-WRAPPER', BlockCardsWrapperComponent);
        this.blocks.addBlock('SEARCH-RESULTS-ALL', BlockClientSearchResultsAllComponent);
        this.blocks.addBlock('USER-SCORING', BlockUserScoringComponent);
        this.blocks.addBlock('HOME-SKINNY-CTA-BANNER', BlockHomeSkinnyCtaBannerComponent);
        this.blocks.addBlock('USER-DOCUMENTS', BlockUserDocumentsComponent);
        this.blocks.addBlock('USER-DATA-TABLE', BlockUserSmpiTableComponent);
        this.blocks.addBlock('USER-AA-TABLE', BlockUserAaTableComponent);
        this.blocks.addBlock('TAB-GROUP', BlockTabGroupComponent);
        this.blocks.addBlock('TESTIMONIAL', BlockTestimonialComponent);
        this.blocks.addBlock('CTA-BANNER', BlockCtaBannerComponent);
        this.blocks.addBlock('HOME-HERO-BANNER-V2', BlockHomeHeroBannerV2Component);
        this.blocks.addBlock('CALLOUT', BlockCalloutComponent);
        this.blocks.addBlock('LEAVE-OF-ABSENCE-TOOL', BlockLeaveOfAbsenceToolComponent);
        this.blocks.addBlock('BENEFIT-PILLARS-SELECT', BlockBenefitPillarsSelectComponent);
        this.blocks.addBlock('CUSTOM-CARDS', BlockCustomCardsComponent);
        this.blocks.addBlock('WISTIA-VIDEO', BlockWistiaVideoComponent);
        this.blocks.addBlock('FEATURE-CARD-GRID', BlockFeatureCardGridComponent);
        this.blocks.addBlock('HERO-BANNER-SPLIT–FEATURE', BlockHeroBannerSplitFeatureComponent);
        this.blocks.addBlock('CONTENT-PAGE-HERO-BANNER', BlockContentPageHeroBannerComponent);
        this.blocks.addBlock('LANDING-LOGIN', BlockLoginLandingComponent);
        this.blocks.addBlock('CONTENT-SELECTION', BlockContentSelectionComponent);
        this.blocks.addBlock('ANNOUNCEMENT-BANNER', BlockAnnouncementBannerComponent);
        this.blocks.addBlock('PULLOUT', BlockPulloutComponent);
        this.blocks.addBlock('CARD-CALLOUTS', BlockCardCalloutsComponent);
        this.blocks.addBlock('ACCORDION', BlockAccordionComponent);
        this.blocks.addBlock('OPEN-ENROLLMENT-BANNER', BlockOpenEnrollmentBannerComponent);
        this.blocks.addBlock('CAROUSEL', BlockCarouselComponent);
        this.blocks.addBlock('TIMELINE', BlockTimelineComponent);
        this.blocks.addBlock('HOME-HERO', BlockHomeHeroBannerComponent);
        this.blocks.addBlock('CONTENT-HERO', BlockContentHeroBannerComponent);
        this.blocks.addBlock('CONTACT-TILES-AND-MODAL', BlockContactModalComponent);
        this.blocks.addBlock('RESOURCES-LIST', BlockResourcesListComponent);
        this.blocks.addBlock('CARD-EVENTS', BlockCardEventsComponent);
    }
}
